import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChildren, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { UniqueValueRenderer } from 'esri/renderers';

import { ESRI } from '../../../esri-loader-util';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

import { getLayerKey } from './layer-key.pipe';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-legend-data-ng',
    styleUrls: ['../../sharedStyles/base.scss', './legend-data.component.scss'],
    templateUrl: './legend-data.component.html'
})
export class LegendDataComponent extends BaseComponent implements OnInit, AfterViewInit {
    SymbolUtils: __esri.symbolUtils;
    @Input() node;

    @ViewChildren('uniqueValueSymbol') uniqueValueSymbolsRef: QueryList<ElementRef<HTMLDivElement>>;

    constructor(_vcr: ViewContainerRef, private readonly layerService: LayerService) {
        super(_vcr);
    }

    async ngOnInit() {
        this.SymbolUtils = await ESRI.SymbolUtils;

        window.addEventListener('symbolChange', (e: any) => {
            if (this.node.data.layerData && this.node.data.layerData.layer && this.node.data.layerData.layer.id === e.detail) {
                this.renderSymbols();
            }
        });
    }

    renderSymbols() {
        if (this.uniqueValueSymbolsRef.toArray().length) {
            this.addRenderToClassBreak(this.uniqueValueSymbolsRef.toArray());
        }
    }

    async ngAfterViewInit() {
        this.uniqueValueSymbolsRef.changes.subscribe(async (previewContainerCollection: QueryList<ElementRef<HTMLDivElement>>) => {
            this.addRenderToClassBreak(previewContainerCollection.toArray());
        });
    }
    private readonly renderPromises: Promise<void>[] = [];

    async addRenderToClassBreak(previewContainers: ElementRef<HTMLDivElement>[]) {
        if (this.renderPromises.length) {
            await Promise.all(this.renderPromises);
            this.renderPromises.length = 0;
        }
        if (previewContainers.length) {
            if (this.node.data && this.node.data.layerData) {
                if (!this.node.data.layerData.layer) {
                    this.renderPromises.push(this.renderLayerSymbol(this.node.data.layerData, previewContainers));
                } else if (this.node.data.layerData.layer && this.node.data.layerData.layer.sublayers) {
                    this.renderPromises.push(this.renderLayerSymbol(this.node.data.layerData.layer.sublayers.toArray()[0], previewContainers));
                }
            }
            await Promise.all(this.renderPromises);
            this.renderPromises.length = 0;
        }
    }

    async renderLayerSymbol(layer: __esri.FeatureLayer | __esri.Sublayer, previewContainers: ElementRef<HTMLDivElement>[]) {
        if (layer.renderer && layer.renderer.type === 'unique-value') {
            const layerKey = getLayerKey(layer);
            const layerPreviewContainers = previewContainers.filter(d => d.nativeElement.dataset.layerKey === layerKey);
            if (layerPreviewContainers.length) {
                const values: { symbol: __esri.Symbol; value: string | number }[] = [...(<UniqueValueRenderer>layer.renderer).uniqueValueInfos] || [];
                if ((<UniqueValueRenderer>layer.renderer).defaultSymbol) {
                    values.unshift({
                        symbol: (<UniqueValueRenderer>layer.renderer).defaultSymbol,
                        value: (<UniqueValueRenderer>layer.renderer).defaultLabel
                    });
                }

                await Promise.all(values.map(async (value) => {
                    const previewContainer = layerPreviewContainers.find(d => d.nativeElement.dataset.value === value.value);

                    if (previewContainer) {
                        const firstChild = previewContainer.nativeElement.childNodes[0];

                        if (firstChild.nodeName === 'DIV') {
                            // Delete the old symbol before rerendering
                            firstChild.remove();
                        }
                        this.layerService.fixPictureSymbols([value.symbol]);
                        const previewHTML = await this.SymbolUtils.renderPreviewHTML(value.symbol, { maxSize: 14, scale: true });
                        if (!previewContainer.nativeElement.querySelectorAll('.value-symbol').length) {
                            previewHTML.className = 'value-symbol';
                            previewContainer.nativeElement.prepend(previewHTML);
                        }
                    }
                }));
            }
        }
    }

    toggleBreakDetails(node) {
        node.data.showBreakDetail = !node.data.showBreakDetail;
    }

    getRoutingDetail(node) {
        return node.data.layerData.mapcoreProps.routingDetail;
    }
}
